import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  Target, 
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  MessageSquare,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300', icon: TrendingUp },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300', icon: CheckCircle2 },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300', icon: AlertCircle }
};

const IMPACT_COLORS = {
  high: 'border-red-500',
  medium: 'border-yellow-500',
  low: 'border-green-500'
};

const ArtistGoals = () => {
  const { isDark } = useTheme();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGoal, setExpandedGoal] = useState(null);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await api.get('/artist/goals');
      setGoals(response.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: goals.length,
    in_progress: goals.filter(g => g.status === 'in_progress').length,
    not_started: goals.filter(g => g.status === 'not_started').length,
    completed: goals.filter(g => g.status === 'completed').length
  };

  const toggleExpand = (goalId) => {
    setExpandedGoal(expandedGoal === goalId ? null : goalId);
  };

  return (
    <ArtistLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="artist-goals">
        {/* Header */}
        <div>
          <h1 className={cn("text-2xl font-bold font-outfit flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
            <Target className="w-7 h-7 text-[#331062]" />
            My Goals
          </h1>
          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Track your assigned goals and progress
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{stats.total}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Total Goals</p>
          </Card>
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className="text-2xl font-bold text-blue-600">{stats.in_progress}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>In Progress</p>
          </Card>
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className="text-2xl font-bold text-slate-500">{stats.not_started}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Not Started</p>
          </Card>
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Completed</p>
          </Card>
        </div>

        {/* Goals List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
          </div>
        ) : goals.length === 0 ? (
          <Card className={cn("p-12 text-center", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <Target className={cn("w-16 h-16 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
            <h3 className={cn("text-lg font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
              No goals assigned yet
            </h3>
            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
              Your manager will assign goals for you to work on
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {goals.map((goal) => {
              const statusConfig = STATUS_CONFIG[goal.status] || STATUS_CONFIG.not_started;
              const StatusIcon = statusConfig.icon;
              const isExpanded = expandedGoal === goal.id;
              
              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className={cn(
                    "overflow-hidden border-l-4",
                    IMPACT_COLORS[goal.impactLevel] || "border-slate-300",
                    isDark ? "bg-slate-800 border-slate-700" : ""
                  )}>
                    {/* Goal Header */}
                    <div 
                      className="p-5 cursor-pointer"
                      onClick={() => toggleExpand(goal.id)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className={cn("text-xs px-2 py-1 rounded-full", statusConfig.color)}>
                              <StatusIcon className="w-3 h-3 inline mr-1" />
                              {statusConfig.label}
                            </span>
                            {goal.impactLevel && (
                              <span className={cn(
                                "text-xs px-2 py-1 rounded-full capitalize",
                                goal.impactLevel === 'high' ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" :
                                goal.impactLevel === 'medium' ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" :
                                "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              )}>
                                {goal.impactLevel} Impact
                              </span>
                            )}
                            {goal.category && (
                              <span className={cn(
                                "text-xs px-2 py-1 rounded-full capitalize",
                                isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                              )}>
                                {goal.category}
                              </span>
                            )}
                          </div>
                          <h3 className={cn("font-semibold text-lg", isDark ? "text-white" : "text-slate-800")}>
                            {goal.title}
                          </h3>
                          {goal.description && (
                            <p className={cn("text-sm mt-1 line-clamp-2", isDark ? "text-slate-400" : "text-slate-500")}>
                              {goal.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {goal.dueDate && (
                            <span className={cn(
                              "text-xs flex items-center gap-1 px-2 py-1 rounded",
                              new Date(goal.dueDate) < new Date() ? "bg-red-100 text-red-700" : (isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600")
                            )}>
                              <Calendar className="w-3 h-3" />
                              {new Date(goal.dueDate).toLocaleDateString()}
                            </span>
                          )}
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      {goal.progress !== undefined && (
                        <div className="mt-4">
                          <div className="flex justify-between items-center mb-1">
                            <span className={cn("text-xs font-medium", isDark ? "text-slate-400" : "text-slate-500")}>
                              Progress
                            </span>
                            <span className={cn("text-xs font-bold", isDark ? "text-white" : "text-slate-700")}>
                              {goal.progress}%
                            </span>
                          </div>
                          <div className={cn("h-2 rounded-full overflow-hidden", isDark ? "bg-slate-700" : "bg-slate-200")}>
                            <div 
                              className={cn(
                                "h-full rounded-full transition-all",
                                goal.progress >= 100 ? "bg-green-500" :
                                goal.progress >= 50 ? "bg-blue-500" :
                                "bg-yellow-500"
                              )}
                              style={{ width: `${Math.min(goal.progress, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Expanded Content */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className={cn("border-t", isDark ? "border-slate-700" : "border-slate-200")}
                        >
                          <div className="p-5 space-y-4">
                            {/* Impact Description */}
                            {goal.impactDescription && (
                              <div>
                                <h4 className={cn("text-sm font-semibold mb-1", isDark ? "text-white" : "text-slate-700")}>
                                  Expected Impact
                                </h4>
                                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                                  {goal.impactDescription}
                                </p>
                              </div>
                            )}

                            {/* Assigned By */}
                            {goal.assignedBy && (
                              <div className="flex items-center gap-2">
                                <span className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                                  Assigned by:
                                </span>
                                <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-slate-700")}>
                                  {goal.assignedBy}
                                </span>
                              </div>
                            )}

                            {/* Comments */}
                            {goal.comments && goal.comments.length > 0 && (
                              <div>
                                <h4 className={cn("text-sm font-semibold mb-2 flex items-center gap-1", isDark ? "text-white" : "text-slate-700")}>
                                  <MessageSquare className="w-4 h-4" />
                                  Comments ({goal.comments.length})
                                </h4>
                                <div className="space-y-2">
                                  {goal.comments.map((comment, index) => (
                                    <div 
                                      key={index}
                                      className={cn("p-3 rounded-lg", isDark ? "bg-slate-700" : "bg-slate-50")}
                                    >
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-slate-700")}>
                                          {comment.user}
                                        </span>
                                        <span className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                                          {new Date(comment.date).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                                        {comment.text}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </ArtistLayout>
  );
};

export default ArtistGoals;
